export type ObjectValues<T> = T[keyof T]

// =============================================================================
// ENVIRONMENT STAGES
// =============================================================================

export type Stage = 'production' | 'staging' | 'development'

// =============================================================================
// RELAY CONFIGURATION
// =============================================================================

/**
 * Main relay URL per stage - this is the primary relay for each environment
 */
export const MAIN_RELAY_BY_STAGE: Record<Stage, string> = {
	production: 'wss://relay.plebeian.market',
	staging: 'wss://relay.staging.plebeian.market',
	development: 'ws://localhost:10547',
}

/**
 * Default public relays that are used in addition to the main relay
 * These are used for reading events from the broader Nostr network
 */
export const DEFAULT_NIP46_RELAYS = [
	{ value: 'wss://relay.plebeian.market', label: 'relay.plebeian.market' },
	{ value: 'wss://relay.nsec.app', label: 'relay.nsec.app' },
	{ value: 'wss://relay.damus.io', label: 'relay.damus.io' },
	{ value: 'wss://nos.lol', label: 'nos.lol' },
	{ value: 'wss://relay.primal.net', label: 'relay.primal.net' },
]

/**
 * Default public relays that are used in addition to the main relay
 * These are used for reading events from the broader Nostr network
 */
export const DEFAULT_PUBLIC_RELAYS: string[] = [
	'wss://sendit.nosflare.com',
	'wss://nostr.mom',
	'wss://nos.lol',
	'wss://relay.nostr.net',
	'wss://relay.damus.io',
	'wss://relay.minibits.cash',
]

// Keep for backward compatibility (deprecated - use DEFAULT_PUBLIC_RELAYS instead)
export const defaultRelaysUrls: string[] = DEFAULT_PUBLIC_RELAYS

// Bug reports relay - always writable even in staging
export const BUG_RELAY = 'wss://bugs.plebeian.market/'

// Dedicated zap detection relays
export const ZAP_RELAYS = [
	'wss://relay.damus.io',
	'wss://nos.lol',
	'wss://relay.nostr.net',
	'wss://relay.minibits.cash',
	'wss://relay.coinos.io',
	'wss://nwc.primal.net',
	'wss://relay.primal.net',
]

export const CURRENCIES = [
	'SATS', // Satoshis
	'BTC', // Bitcoin
	'USD', // United States Dollar
	'EUR', // Euro
	'JPY', // Japanese Yen
	'GBP', // Pound Sterling
	'CHF', // Swiss Franc
	'CNY', // Chinese Renminbi (RMB)
	'AUD', // Australian Dollar
	'CAD', // Canadian Dollar
	'HKD', // Hong Kong Dollar
	'SGD', // Singapore Dollar
	'INR', // Indian Rupee
	'MXN', // Mexican Peso
	'RUB', // Russian Ruble
	'BRL', // Brazilian Real
	'TRY', // Turkish Lira
	'KRW', // South Korean Won
	'ZAR', // South African Rand
	'ARS', // Argentine Peso
	'CLP', // Chilean Peso
	'COP', // Colombian Peso
	'PEN', // Peruvian Sol
	'UYU', // Uruguayan Peso
	'PHP', // Philippine Peso
	'THB', // Thai Baht
	'IDR', // Indonesian Rupiah
	'MYR', // Malaysian Ringgit
	'NGN', // Nigerian Naira
] as const

const CURRENCY_CVM_RELAYS = ['wss://relay.contextvm.org']

export function getCurrencyServerRelays(): string[] {
	const env = process.env.NODE_ENV || 'development'
	switch (env) {
		case 'production':
			return [...CURRENCY_CVM_RELAYS]
		case 'staging':
			return [...CURRENCY_CVM_RELAYS]
		default:
			return ['ws://localhost:10547', ...CURRENCY_CVM_RELAYS]
	}
}

export const CVM_SERVER_PUBKEY = process.env.CVM_SERVER_PUBKEY || '29bd6461f780c07b29c89b4df8017db90973d5608a3cd811a0522b15c1064f15'

export const DEFAULT_ZAP_AMOUNTS = [
	{ displayText: '😊 21 sats', amount: 21 },
	{ displayText: '🚀 210 sats', amount: 210 },
	{ displayText: '🔥 2100 sats', amount: 2100 },
]

export const HEX_KEYS_REGEX = /^(?:[0-9a-fA-F]{64})$/
export const EMAIL_REGEX = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

export const PAYMENT_DETAILS_METHOD = {
	LIGHTNING_NETWORK: 'ln',
	// ON_CHAIN: 'on-chain',
	// CASHU: 'cashu',
	// OTHER: 'other',
} as const

export type PaymentDetailsMethod = ObjectValues<typeof PAYMENT_DETAILS_METHOD>

export const COUNTRIES_ISO = {
	AD: {
		languages: ['ca'],
		name: 'Andorra',
		original: 'Andorra',
		iso3: 'AND',
	},
	AE: {
		languages: ['ar'],
		name: 'United Arab Emirates',
		original: 'دولة الإمارات العربية المتحدة',
		iso3: 'ARE',
	},
	AF: {
		languages: ['ps', 'uz', 'tk'],
		name: 'Afghanistan',
		original: 'افغانستان',
		iso3: 'AFG',
	},
	AG: {
		languages: ['en'],
		name: 'Antigua and Barbuda',
		original: 'Antigua and Barbuda',
		iso3: 'ATG',
	},
	AI: {
		languages: ['en'],
		name: 'Anguilla',
		original: 'Anguilla',
		iso3: 'AIA',
	},
	AL: {
		languages: ['sq'],
		name: 'Albania',
		original: 'Shqipëria',
		iso3: 'ALB',
	},
	AM: {
		languages: ['hy', 'ru'],
		name: 'Armenia',
		original: 'Հայաստան',
		iso3: 'ARM',
	},
	AO: {
		languages: ['pt'],
		name: 'Angola',
		original: 'Angola',
		iso3: 'AGO',
	},
	AQ: {
		languages: [],
		name: 'Antarctica',
		original: 'Antarctica',
		iso3: 'ATA',
	},
	AR: {
		languages: ['es', 'gn'],
		name: 'Argentina',
		original: 'Argentina',
		iso3: 'ARG',
	},
	AS: {
		languages: ['en', 'sm'],
		name: 'American Samoa',
		original: 'American Samoa',
		iso3: 'ASM',
	},
	AT: {
		languages: ['de'],
		name: 'Austria',
		original: 'Österreich',
		iso3: 'AUT',
	},
	AU: {
		languages: ['en'],
		name: 'Australia',
		original: 'Australia',
		iso3: 'AUS',
	},
	AW: {
		languages: ['nl', 'pa'],
		name: 'Aruba',
		original: 'Aruba',
		iso3: 'ABW',
	},
	AX: {
		languages: ['sv'],
		name: 'Aland',
		original: 'Åland',
		iso3: 'ALA',
	},
	AZ: {
		languages: ['az'],
		name: 'Azerbaijan',
		original: 'Azərbaycan',
		iso3: 'AZE',
	},
	BA: {
		languages: ['bs', 'hr', 'sr'],
		name: 'Bosnia and Herzegovina',
		original: 'Bosna i Hercegovina',
		iso3: 'BIH',
	},
	BB: {
		languages: ['en'],
		name: 'Barbados',
		original: 'Barbados',
		iso3: 'BRB',
	},
	BD: {
		languages: ['bn'],
		name: 'Bangladesh',
		original: 'Bangladesh',
		iso3: 'BGD',
	},
	BE: {
		languages: ['nl', 'fr', 'de'],
		name: 'Belgium',
		original: 'België',
		iso3: 'BEL',
	},
	BF: {
		languages: ['fr', 'ff'],
		name: 'Burkina Faso',
		original: 'Burkina Faso',
		iso3: 'BFA',
	},
	BG: {
		languages: ['bg'],
		name: 'Bulgaria',
		original: 'България',
		iso3: 'BGR',
	},
	BH: {
		languages: ['ar'],
		name: 'Bahrain',
		original: 'البحرين',
		iso3: 'BHR',
	},
	BI: {
		languages: ['fr', 'rn'],
		name: 'Burundi',
		original: 'Burundi',
		iso3: 'BDI',
	},
	BJ: {
		languages: ['fr'],
		name: 'Benin',
		original: 'Bénin',
		iso3: 'BEN',
	},
	BL: {
		languages: ['fr'],
		name: 'Saint Barthelemy',
		original: 'Saint-Barthélemy',
		iso3: 'BLM',
	},
	BM: {
		languages: ['en'],
		name: 'Bermuda',
		original: 'Bermuda',
		iso3: 'BMU',
	},
	BN: {
		languages: ['ms'],
		name: 'Brunei',
		original: 'Negara Brunei Darussalam',
		iso3: 'BRN',
	},
	BO: {
		languages: ['es', 'ay', 'qu'],
		name: 'Bolivia',
		original: 'Bolivia',
		iso3: 'BOL',
	},
	BQ: {
		languages: ['nl'],
		name: 'Bonaire',
		original: 'Bonaire',
		iso3: 'BES',
	},
	BR: {
		languages: ['pt'],
		name: 'Brazil',
		original: 'Brasil',
		iso3: 'BRA',
	},
	BS: {
		languages: ['en'],
		name: 'Bahamas',
		original: 'Bahamas',
		iso3: 'BHS',
	},
	BT: {
		languages: ['dz'],
		name: 'Bhutan',
		original: 'ʼbrug-yul',
		iso3: 'BTN',
	},
	BV: {
		languages: ['no', 'nb', 'nn'],
		name: 'Bouvet Island',
		original: 'Bouvetøya',
		iso3: 'BVT',
	},
	BW: {
		languages: ['en', 'tn'],
		name: 'Botswana',
		original: 'Botswana',
		iso3: 'BWA',
	},
	BY: {
		languages: ['be', 'ru'],
		name: 'Belarus',
		original: 'Беларусь',
		iso3: 'BLR',
	},
	BZ: {
		languages: ['en', 'es'],
		name: 'Belize',
		original: 'Belize',
		iso3: 'BLZ',
	},
	CA: {
		languages: ['en', 'fr'],
		name: 'Canada',
		original: 'Canada',
		iso3: 'CAN',
	},
	CC: {
		languages: ['en'],
		name: 'Cocos (Keeling) Islands',
		original: 'Cocos (Keeling) Islands',
		iso3: 'CCK',
	},
	CD: {
		languages: ['fr', 'ln', 'kg', 'sw', 'lu'],
		name: 'Democratic Republic of the Congo',
		original: 'République démocratique du Congo',
		iso3: 'COD',
	},
	CF: {
		languages: ['fr', 'sg'],
		name: 'Central African Republic',
		original: 'Ködörösêse tî Bêafrîka',
		iso3: 'CAF',
	},
	CG: {
		languages: ['fr', 'ln'],
		name: 'Republic of the Congo',
		original: 'République du Congo',
		iso3: 'COG',
	},
	CH: {
		languages: ['de', 'fr', 'it'],
		name: 'Switzerland',
		original: 'Schweiz',
		iso3: 'CHE',
	},
	CI: {
		languages: ['fr'],
		name: 'Ivory Coast',
		original: "Côte d'Ivoire",
		iso3: 'CIV',
	},
	CK: {
		languages: ['en'],
		name: 'Cook Islands',
		original: 'Cook Islands',
		iso3: 'COK',
	},
	CL: {
		languages: ['es'],
		name: 'Chile',
		original: 'Chile',
		iso3: 'CHL',
	},
	CM: {
		languages: ['en', 'fr'],
		name: 'Cameroon',
		original: 'Cameroon',
		iso3: 'CMR',
	},
	CN: {
		languages: ['zh'],
		name: 'China',
		original: '中国',
		iso3: 'CHN',
	},
	CO: {
		languages: ['es'],
		name: 'Colombia',
		original: 'Colombia',
		iso3: 'COL',
	},
	CR: {
		languages: ['es'],
		name: 'Costa Rica',
		original: 'Costa Rica',
		iso3: 'CRI',
	},
	CU: {
		languages: ['es'],
		name: 'Cuba',
		original: 'Cuba',
		iso3: 'CUB',
	},
	CV: {
		languages: ['pt'],
		name: 'Cape Verde',
		original: 'Cabo Verde',
		iso3: 'CPV',
	},
	CW: {
		languages: ['nl', 'pa', 'en'],
		name: 'Curacao',
		original: 'Curaçao',
		iso3: 'CUW',
	},
	CX: {
		languages: ['en'],
		name: 'Christmas Island',
		original: 'Christmas Island',
		iso3: 'CXR',
	},
	CY: {
		languages: ['el', 'tr', 'hy'],
		name: 'Cyprus',
		original: 'Κύπρος',
		iso3: 'CYP',
	},
	CZ: {
		languages: ['cs'],
		name: 'Czech Republic',
		original: 'Česká republika',
		iso3: 'CZE',
	},
	DE: {
		languages: ['de'],
		name: 'Germany',
		original: 'Deutschland',
		iso3: 'DEU',
	},
	DJ: {
		languages: ['fr', 'ar'],
		name: 'Djibouti',
		original: 'Djibouti',
		iso3: 'DJI',
	},
	DK: {
		languages: ['da'],
		name: 'Denmark',
		original: 'Danmark',
		iso3: 'DNK',
	},
	DM: {
		languages: ['en'],
		name: 'Dominica',
		original: 'Dominica',
		iso3: 'DMA',
	},
	DO: {
		languages: ['es'],
		name: 'Dominican Republic',
		original: 'República Dominicana',
		iso3: 'DOM',
	},
	DZ: {
		languages: ['ar'],
		name: 'Algeria',
		original: 'الجزائر',
		iso3: 'DZA',
	},
	EC: {
		languages: ['es'],
		name: 'Ecuador',
		original: 'Ecuador',
		iso3: 'ECU',
	},
	EE: {
		languages: ['et'],
		name: 'Estonia',
		original: 'Eesti',
		iso3: 'EST',
	},
	EG: {
		languages: ['ar'],
		name: 'Egypt',
		original: 'مصر�',
		iso3: 'EGY',
	},
	EH: {
		languages: ['es'],
		name: 'Western Sahara',
		original: 'الصحراء الغربية',
		iso3: 'ESH',
	},
	ER: {
		languages: ['ti', 'ar', 'en'],
		name: 'Eritrea',
		original: 'ኤርትራ',
		iso3: 'ERI',
	},
	ES: {
		languages: ['es', 'eu', 'ca', 'gl', 'oc'],
		name: 'Spain',
		original: 'España',
		iso3: 'ESP',
	},
	ET: {
		languages: ['am'],
		name: 'Ethiopia',
		original: 'ኢትዮጵያ',
		iso3: 'ETH',
	},
	FI: {
		languages: ['fi', 'sv'],
		name: 'Finland',
		original: 'Suomi',
		iso3: 'FIN',
	},
	FJ: {
		languages: ['en', 'fj', 'hi', 'ur'],
		name: 'Fiji',
		original: 'Fiji',
		iso3: 'FJI',
	},
	FK: {
		languages: ['en'],
		name: 'Falkland Islands',
		original: 'Falkland Islands',
		iso3: 'FLK',
	},
	FM: {
		languages: ['en'],
		name: 'Micronesia',
		original: 'Micronesia',
		iso3: 'FSM',
	},
	FO: {
		languages: ['fo'],
		name: 'Faroe Islands',
		original: 'Føroyar',
		iso3: 'FRO',
	},
	FR: {
		languages: ['fr'],
		name: 'France',
		original: 'France',
		iso3: 'FRA',
	},
	GA: {
		languages: ['fr'],
		name: 'Gabon',
		original: 'Gabon',
		iso3: 'GAB',
	},
	GB: {
		languages: ['en'],
		name: 'United Kingdom',
		original: 'United Kingdom',
		iso3: 'GBR',
	},
	GD: {
		languages: ['en'],
		name: 'Grenada',
		original: 'Grenada',
		iso3: 'GRD',
	},
	GE: {
		languages: ['ka'],
		name: 'Georgia',
		original: 'საქართველო',
		iso3: 'GEO',
	},
	GF: {
		languages: ['fr'],
		name: 'French Guiana',
		original: 'Guyane française',
		iso3: 'GUF',
	},
	GG: {
		languages: ['en', 'fr'],
		name: 'Guernsey',
		original: 'Guernsey',
		iso3: 'GGY',
	},
	GH: {
		languages: ['en'],
		name: 'Ghana',
		original: 'Ghana',
		iso3: 'GHA',
	},
	GI: {
		languages: ['en'],
		name: 'Gibraltar',
		original: 'Gibraltar',
		iso3: 'GIB',
	},
	GL: {
		languages: ['kl'],
		name: 'Greenland',
		original: 'Kalaallit Nunaat',
		iso3: 'GRL',
	},
	GM: {
		languages: ['en'],
		name: 'Gambia',
		original: 'Gambia',
		iso3: 'GMB',
	},
	GN: {
		languages: ['fr', 'ff'],
		name: 'Guinea',
		original: 'Guinée',
		iso3: 'GIN',
	},
	GP: {
		languages: ['fr'],
		name: 'Guadeloupe',
		original: 'Guadeloupe',
		iso3: 'GLP',
	},
	GQ: {
		languages: ['es', 'fr'],
		name: 'Equatorial Guinea',
		original: 'Guinea Ecuatorial',
		iso3: 'GNQ',
	},
	GR: {
		languages: ['el'],
		name: 'Greece',
		original: 'Ελλάδα',
		iso3: 'GRC',
	},
	GS: {
		languages: ['en'],
		name: 'South Georgia and the South Sandwich Islands',
		original: 'South Georgia',
		iso3: 'SGS',
	},
	GT: {
		languages: ['es'],
		name: 'Guatemala',
		original: 'Guatemala',
		iso3: 'GTM',
	},
	GU: {
		languages: ['en', 'ch', 'es'],
		name: 'Guam',
		original: 'Guam',
		iso3: 'GUM',
	},
	GW: {
		languages: ['pt'],
		name: 'Guinea-Bissau',
		original: 'Guiné-Bissau',
		iso3: 'GNB',
	},
	GY: {
		languages: ['en'],
		name: 'Guyana',
		original: 'Guyana',
		iso3: 'GUY',
	},
	HK: {
		languages: ['zh', 'en'],
		name: 'Hong Kong',
		original: '香港',
		iso3: 'HKG',
	},
	HM: {
		languages: ['en'],
		name: 'Heard Island and McDonald Islands',
		original: 'Heard Island and McDonald Islands',
		iso3: 'HMD',
	},
	HN: {
		languages: ['es'],
		name: 'Honduras',
		original: 'Honduras',
		iso3: 'HND',
	},
	HR: {
		languages: ['hr'],
		name: 'Croatia',
		original: 'Hrvatska',
		iso3: 'HRV',
	},
	HT: {
		languages: ['fr', 'ht'],
		name: 'Haiti',
		original: 'Haïti',
		iso3: 'HTI',
	},
	HU: {
		languages: ['hu'],
		name: 'Hungary',
		original: 'Magyarország',
		iso3: 'HUN',
	},
	ID: {
		languages: ['id'],
		name: 'Indonesia',
		original: 'Indonesia',
		iso3: 'IDN',
	},
	IE: {
		languages: ['ga', 'en'],
		name: 'Ireland',
		original: 'Éire',
		iso3: 'IRL',
	},
	IL: {
		languages: ['he', 'ar'],
		name: 'Israel',
		original: 'ישראל',
		iso3: 'ISR',
	},
	IM: {
		languages: ['en', 'gv'],
		name: 'Isle of Man',
		original: 'Isle of Man',
		iso3: 'IMN',
	},
	IN: {
		languages: ['hi', 'en'],
		name: 'India',
		original: 'भरत',
		iso3: 'IND',
	},
	IO: {
		languages: ['en'],
		name: 'British Indian Ocean Territory',
		original: 'British Indian Ocean Territory',
		iso3: 'IOT',
	},
	IQ: {
		languages: ['ar', 'ku'],
		name: 'Iraq',
		original: 'العراق',
		iso3: 'IRQ',
	},
	IR: {
		languages: ['fa'],
		name: 'Iran',
		original: 'ایران',
		iso3: 'IRN',
	},
	IS: {
		languages: ['is'],
		name: 'Iceland',
		original: 'Ísland',
		iso3: 'ISL',
	},
	IT: {
		languages: ['it'],
		name: 'Italy',
		original: 'Italia',
		iso3: 'ITA',
	},
	JE: {
		languages: ['en', 'fr'],
		name: 'Jersey',
		original: 'Jersey',
		iso3: 'JEY',
	},
	JM: {
		languages: ['en'],
		name: 'Jamaica',
		original: 'Jamaica',
		iso3: 'JAM',
	},
	JO: {
		languages: ['ar'],
		name: 'Jordan',
		original: 'الأردن',
		iso3: 'JOR',
	},
	JP: {
		languages: ['ja'],
		name: 'Japan',
		original: '日本',
		iso3: 'JPN',
	},
	KE: {
		languages: ['en', 'sw'],
		name: 'Kenya',
		original: 'Kenya',
		iso3: 'KEN',
	},
	KG: {
		languages: ['ky', 'ru'],
		name: 'Kyrgyzstan',
		original: 'Кыргызстан',
		iso3: 'KGZ',
	},
	KH: {
		languages: ['km'],
		name: 'Cambodia',
		original: 'Kâmpŭchéa',
		iso3: 'KHM',
	},
	KI: {
		languages: ['en'],
		name: 'Kiribati',
		original: 'Kiribati',
		iso3: 'KIR',
	},
	KM: {
		languages: ['ar', 'fr'],
		name: 'Comoros',
		original: 'Komori',
		iso3: 'COM',
	},
	KN: {
		languages: ['en'],
		name: 'Saint Kitts and Nevis',
		original: 'Saint Kitts and Nevis',
		iso3: 'KNA',
	},
	KP: {
		languages: ['ko'],
		name: 'North Korea',
		original: '북한',
		iso3: 'PRK',
	},
	KR: {
		languages: ['ko'],
		name: 'South Korea',
		original: '대한민국',
		iso3: 'KOR',
	},
	KW: {
		languages: ['ar'],
		name: 'Kuwait',
		original: 'الكويت',
		iso3: 'KWT',
	},
	KY: {
		languages: ['en'],
		name: 'Cayman Islands',
		original: 'Cayman Islands',
		iso3: 'CYM',
	},
	KZ: {
		languages: ['kk', 'ru'],
		name: 'Kazakhstan',
		original: 'Қазақстан',
		iso3: 'KAZ',
	},
	LA: {
		languages: ['lo'],
		name: 'Laos',
		original: 'ສປປລາວ',
		iso3: 'LAO',
	},
	LB: {
		languages: ['ar', 'fr'],
		name: 'Lebanon',
		original: 'لبنان',
		iso3: 'LBN',
	},
	LC: {
		languages: ['en'],
		name: 'Saint Lucia',
		original: 'Saint Lucia',
		iso3: 'LCA',
	},
	LI: {
		languages: ['de'],
		name: 'Liechtenstein',
		original: 'Liechtenstein',
		iso3: 'LIE',
	},
	LK: {
		languages: ['si', 'ta'],
		name: 'Sri Lanka',
		original: 'śrī laṃkāva',
		iso3: 'LKA',
	},
	LR: {
		languages: ['en'],
		name: 'Liberia',
		original: 'Liberia',
		iso3: 'LBR',
	},
	LS: {
		languages: ['en', 'st'],
		name: 'Lesotho',
		original: 'Lesotho',
		iso3: 'LSO',
	},
	LT: {
		languages: ['lt'],
		name: 'Lithuania',
		original: 'Lietuva',
		iso3: 'LTU',
	},
	LU: {
		languages: ['fr', 'de', 'lb'],
		name: 'Luxembourg',
		original: 'Luxembourg',
		iso3: 'LUX',
	},
	LV: {
		languages: ['lv'],
		name: 'Latvia',
		original: 'Latvija',
		iso3: 'LVA',
	},
	LY: {
		languages: ['ar'],
		name: 'Libya',
		original: 'ليبيا',
		iso3: 'LBY',
	},
	MA: {
		languages: ['ar'],
		name: 'Morocco',
		original: 'المغرب',
		iso3: 'MAR',
	},
	MC: {
		languages: ['fr'],
		name: 'Monaco',
		original: 'Monaco',
		iso3: 'MCO',
	},
	MD: {
		languages: ['ro'],
		name: 'Moldova',
		original: 'Moldova',
		iso3: 'MDA',
	},
	ME: {
		languages: ['sr', 'bs', 'sq', 'hr'],
		name: 'Montenegro',
		original: 'Црна Гора',
		iso3: 'MNE',
	},
	MF: {
		languages: ['en', 'fr', 'nl'],
		name: 'Saint Martin',
		original: 'Saint-Martin',
		iso3: 'MAF',
	},
	MG: {
		languages: ['fr', 'mg'],
		name: 'Madagascar',
		original: 'Madagasikara',
		iso3: 'MDG',
	},
	MH: {
		languages: ['en', 'mh'],
		name: 'Marshall Islands',
		original: 'Majeļ',
		iso3: 'MHL',
	},
	MK: {
		languages: ['mk'],
		name: 'North Macedonia',
		original: 'Северна Македонија',
		iso3: 'MKD',
	},
	ML: {
		languages: ['fr'],
		name: 'Mali',
		original: 'Mali',
		iso3: 'MLI',
	},
	MM: {
		languages: ['my'],
		name: 'Myanmar (Burma)',
		original: 'မနမာ',
		iso3: 'MMR',
	},
	MN: {
		languages: ['mn'],
		name: 'Mongolia',
		original: 'Монгол улс',
		iso3: 'MNG',
	},
	MO: {
		languages: ['zh', 'pt'],
		name: 'Macao',
		original: '澳門',
		iso3: 'MAC',
	},
	MP: {
		languages: ['en', 'ch'],
		name: 'Northern Mariana Islands',
		original: 'Northern Mariana Islands',
		iso3: 'MNP',
	},
	MQ: {
		languages: ['fr'],
		name: 'Martinique',
		original: 'Martinique',
		iso3: 'MTQ',
	},
	MR: {
		languages: ['ar'],
		name: 'Mauritania',
		original: 'موريتانيا',
		iso3: 'MRT',
	},
	MS: {
		languages: ['en'],
		name: 'Montserrat',
		original: 'Montserrat',
		iso3: 'MSR',
	},
	MT: {
		languages: ['mt', 'en'],
		name: 'Malta',
		original: 'Malta',
		iso3: 'MLT',
	},
	MU: {
		languages: ['en'],
		name: 'Mauritius',
		original: 'Maurice',
		iso3: 'MUS',
	},
	MV: {
		languages: ['dv'],
		name: 'Maldives',
		original: 'Maldives',
		iso3: 'MDV',
	},
	MW: {
		languages: ['en', 'ny'],
		name: 'Malawi',
		original: 'Malawi',
		iso3: 'MWI',
	},
	MX: {
		languages: ['es'],
		name: 'Mexico',
		original: 'México',
		iso3: 'MEX',
	},
	MY: {
		languages: ['ms'],
		name: 'Malaysia',
		original: 'Malaysia',
		iso3: 'MYS',
	},
	MZ: {
		languages: ['pt'],
		name: 'Mozambique',
		original: 'Moçambique',
		iso3: 'MOZ',
	},
	NA: {
		languages: ['en', 'af'],
		name: 'Namibia',
		original: 'Namibia',
		iso3: 'NAM',
	},
	NC: {
		languages: ['fr'],
		name: 'New Caledonia',
		original: 'Nouvelle-Calédonie',
		iso3: 'NCL',
	},
	NE: {
		languages: ['fr'],
		name: 'Niger',
		original: 'Niger',
		iso3: 'NER',
	},
	NF: {
		languages: ['en'],
		name: 'Norfolk Island',
		original: 'Norfolk Island',
		iso3: 'NFK',
	},
	NG: {
		languages: ['en'],
		name: 'Nigeria',
		original: 'Nigeria',
		iso3: 'NGA',
	},
	NI: {
		languages: ['es'],
		name: 'Nicaragua',
		original: 'Nicaragua',
		iso3: 'NIC',
	},
	NL: {
		languages: ['nl'],
		name: 'Netherlands',
		original: 'Nederland',
		iso3: 'NLD',
	},
	NO: {
		languages: ['no', 'nb', 'nn'],
		name: 'Norway',
		original: 'Norge',
		iso3: 'NOR',
	},
	NP: {
		languages: ['ne'],
		name: 'Nepal',
		original: 'नपल',
		iso3: 'NPL',
	},
	NR: {
		languages: ['en', 'na'],
		name: 'Nauru',
		original: 'Nauru',
		iso3: 'NRU',
	},
	NU: {
		languages: ['en'],
		name: 'Niue',
		original: 'Niuē',
		iso3: 'NIU',
	},
	NZ: {
		languages: ['en', 'mi'],
		name: 'New Zealand',
		original: 'New Zealand',
		iso3: 'NZL',
	},
	OM: {
		languages: ['ar'],
		name: 'Oman',
		original: 'عمان',
		iso3: 'OMN',
	},
	PA: {
		languages: ['es'],
		name: 'Panama',
		original: 'Panamá',
		iso3: 'PAN',
	},
	PE: {
		languages: ['es'],
		name: 'Peru',
		original: 'Perú',
		iso3: 'PER',
	},
	PF: {
		languages: ['fr'],
		name: 'French Polynesia',
		original: 'Polynésie française',
		iso3: 'PYF',
	},
	PG: {
		languages: ['en'],
		name: 'Papua New Guinea',
		original: 'Papua Niugini',
		iso3: 'PNG',
	},
	PH: {
		languages: ['en'],
		name: 'Philippines',
		original: 'Pilipinas',
		iso3: 'PHL',
	},
	PK: {
		languages: ['en', 'ur'],
		name: 'Pakistan',
		original: 'Pakistan',
		iso3: 'PAK',
	},
	PL: {
		languages: ['pl'],
		name: 'Poland',
		original: 'Polska',
		iso3: 'POL',
	},
	PM: {
		languages: ['fr'],
		name: 'Saint Pierre and Miquelon',
		original: 'Saint-Pierre-et-Miquelon',
		iso3: 'SPM',
	},
	PN: {
		languages: ['en'],
		name: 'Pitcairn Islands',
		original: 'Pitcairn Islands',
		iso3: 'PCN',
	},
	PR: {
		languages: ['es', 'en'],
		name: 'Puerto Rico',
		original: 'Puerto Rico',
		iso3: 'PRI',
	},
	PS: {
		languages: ['ar'],
		name: 'Palestine',
		original: 'فلسطين',
		iso3: 'PSE',
	},
	PT: {
		languages: ['pt'],
		name: 'Portugal',
		original: 'Portugal',
		iso3: 'PRT',
	},
	PW: {
		languages: ['en'],
		name: 'Palau',
		original: 'Palau',
		iso3: 'PLW',
	},
	PY: {
		languages: ['es', 'gn'],
		name: 'Paraguay',
		original: 'Paraguay',
		iso3: 'PRY',
	},
	QA: {
		languages: ['ar'],
		name: 'Qatar',
		original: 'قطر',
		iso3: 'QAT',
	},
	RE: {
		languages: ['fr'],
		name: 'Reunion',
		original: 'La Réunion',
		iso3: 'REU',
	},
	RO: {
		languages: ['ro'],
		name: 'Romania',
		original: 'România',
		iso3: 'ROU',
	},
	RS: {
		languages: ['sr'],
		name: 'Serbia',
		original: 'Србија',
		iso3: 'SRB',
	},
	RU: {
		languages: ['ru'],
		name: 'Russia',
		original: 'Россия',
		iso3: 'RUS',
	},
	RW: {
		languages: ['rw', 'en', 'fr'],
		name: 'Rwanda',
		original: 'Rwanda',
		iso3: 'RWA',
	},
	SA: {
		languages: ['ar'],
		name: 'Saudi Arabia',
		original: 'العربية السعودية',
		iso3: 'SAU',
	},
	SB: {
		languages: ['en'],
		name: 'Solomon Islands',
		original: 'Solomon Islands',
		iso3: 'SLB',
	},
	SC: {
		languages: ['fr', 'en'],
		name: 'Seychelles',
		original: 'Seychelles',
		iso3: 'SYC',
	},
	SD: {
		languages: ['ar', 'en'],
		name: 'Sudan',
		original: 'السودان',
		iso3: 'SDN',
	},
	SE: {
		languages: ['sv'],
		name: 'Sweden',
		original: 'Sverige',
		iso3: 'SWE',
	},
	SG: {
		languages: ['en', 'ms', 'ta', 'zh'],
		name: 'Singapore',
		original: 'Singapore',
		iso3: 'SGP',
	},
	SH: {
		languages: ['en'],
		name: 'Saint Helena',
		original: 'Saint Helena',
		iso3: 'SHN',
	},
	SI: {
		languages: ['sl'],
		name: 'Slovenia',
		original: 'Slovenija',
		iso3: 'SVN',
	},
	SJ: {
		languages: ['no'],
		name: 'Svalbard and Jan Mayen',
		original: 'Svalbard og Jan Mayen',
		iso3: 'SJM',
	},
	SK: {
		languages: ['sk'],
		name: 'Slovakia',
		original: 'Slovensko',
		iso3: 'SVK',
	},
	SL: {
		languages: ['en'],
		name: 'Sierra Leone',
		original: 'Sierra Leone',
		iso3: 'SLE',
	},
	SM: {
		languages: ['it'],
		name: 'San Marino',
		original: 'San Marino',
		iso3: 'SMR',
	},
	SN: {
		languages: ['fr'],
		name: 'Senegal',
		original: 'Sénégal',
		iso3: 'SEN',
	},
	SO: {
		languages: ['so', 'ar'],
		name: 'Somalia',
		original: 'Soomaaliya',
		iso3: 'SOM',
	},
	SR: {
		languages: ['nl'],
		name: 'Suriname',
		original: 'Suriname',
		iso3: 'SUR',
	},
	SS: {
		languages: ['en'],
		name: 'South Sudan',
		original: 'South Sudan',
		iso3: 'SSD',
	},
	ST: {
		languages: ['pt'],
		name: 'Sao Tome and Principe',
		original: 'São Tomé e Príncipe',
		iso3: 'STP',
	},
	SV: {
		languages: ['es'],
		name: 'El Salvador',
		original: 'El Salvador',
		iso3: 'SLV',
	},
	SX: {
		languages: ['nl', 'en'],
		name: 'Sint Maarten',
		original: 'Sint Maarten',
		iso3: 'SXM',
	},
	SY: {
		languages: ['ar'],
		name: 'Syria',
		original: 'سوريا',
		iso3: 'SYR',
	},
	SZ: {
		languages: ['en', 'ss'],
		name: 'Eswatini',
		original: 'Eswatini',
		iso3: 'SWZ',
	},
	TC: {
		languages: ['en'],
		name: 'Turks and Caicos Islands',
		original: 'Turks and Caicos Islands',
		iso3: 'TCA',
	},
	TD: {
		languages: ['fr', 'ar'],
		name: 'Chad',
		original: 'Tchad',
		iso3: 'TCD',
	},
	TF: {
		languages: ['fr'],
		name: 'French Southern Territories',
		original: 'Territoire des Terres australes et antarctiques fr',
		iso3: 'ATF',
	},
	TG: {
		languages: ['fr'],
		name: 'Togo',
		original: 'Togo',
		iso3: 'TGO',
	},
	TH: {
		languages: ['th'],
		name: 'Thailand',
		original: 'ประเทศไทย',
		iso3: 'THA',
	},
	TJ: {
		languages: ['tg', 'ru'],
		name: 'Tajikistan',
		original: 'Тоҷикистон',
		iso3: 'TJK',
	},
	TK: {
		languages: ['en'],
		name: 'Tokelau',
		original: 'Tokelau',
		iso3: 'TKL',
	},
	TL: {
		languages: ['pt'],
		name: 'East Timor',
		original: 'Timor-Leste',
		iso3: 'TLS',
	},
	TM: {
		languages: ['tk', 'ru'],
		name: 'Turkmenistan',
		original: 'Türkmenistan',
		iso3: 'TKM',
	},
	TN: {
		languages: ['ar'],
		name: 'Tunisia',
		original: 'تونس',
		iso3: 'TUN',
	},
	TO: {
		languages: ['en', 'to'],
		name: 'Tonga',
		original: 'Tonga',
		iso3: 'TON',
	},
	TR: {
		languages: ['tr'],
		name: 'Turkey',
		original: 'Türkiye',
		iso3: 'TUR',
	},
	TT: {
		languages: ['en'],
		name: 'Trinidad and Tobago',
		original: 'Trinidad and Tobago',
		iso3: 'TTO',
	},
	TV: {
		languages: ['en'],
		name: 'Tuvalu',
		original: 'Tuvalu',
		iso3: 'TUV',
	},
	TW: {
		languages: ['zh'],
		name: 'Taiwan',
		original: '臺灣',
		iso3: 'TWN',
	},
	TZ: {
		languages: ['sw', 'en'],
		name: 'Tanzania',
		original: 'Tanzania',
		iso3: 'TZA',
	},
	UA: {
		languages: ['uk'],
		name: 'Ukraine',
		original: 'Україна',
		iso3: 'UKR',
	},
	UG: {
		languages: ['en', 'sw'],
		name: 'Uganda',
		original: 'Uganda',
		iso3: 'UGA',
	},
	UM: {
		languages: ['en'],
		name: 'U.S. Minor Outlying Islands',
		original: 'United States Minor Outlying Islands',
		iso3: 'UMI',
	},
	US: {
		languages: ['en'],
		name: 'United States',
		original: 'United States',
		iso3: 'USA',
	},
	UY: {
		languages: ['es'],
		name: 'Uruguay',
		original: 'Uruguay',
		iso3: 'URY',
	},
	UZ: {
		languages: ['uz', 'ru'],
		name: 'Uzbekistan',
		original: "O'zbekiston",
		iso3: 'UZB',
	},
	VA: {
		languages: ['it', 'la'],
		name: 'Vatican City',
		original: 'Vaticano',
		iso3: 'VAT',
	},
	VC: {
		languages: ['en'],
		name: 'Saint Vincent and the Grenadines',
		original: 'Saint Vincent and the Grenadines',
		iso3: 'VCT',
	},
	VE: {
		languages: ['es'],
		name: 'Venezuela',
		original: 'Venezuela',
		iso3: 'VEN',
	},
	VG: {
		languages: ['en'],
		name: 'British Virgin Islands',
		original: 'British Virgin Islands',
		iso3: 'VGB',
	},
	VI: {
		languages: ['en'],
		name: 'U.S. Virgin Islands',
		original: 'United States Virgin Islands',
		iso3: 'VIR',
	},
	VN: {
		languages: ['vi'],
		name: 'Vietnam',
		original: 'Việt Nam',
		iso3: 'VNM',
	},
	VU: {
		languages: ['bi', 'en', 'fr'],
		name: 'Vanuatu',
		original: 'Vanuatu',
		iso3: 'VUT',
	},
	WF: {
		languages: ['fr'],
		name: 'Wallis and Futuna',
		original: 'Wallis et Futuna',
		iso3: 'WLF',
	},
	WS: {
		languages: ['sm', 'en'],
		name: 'Samoa',
		original: 'Samoa',
		iso3: 'WSM',
	},
	XK: {
		languages: ['sq', 'sr'],
		name: 'Kosovo',
		original: 'Republika e Kosovës',
		iso3: 'XKX',
	},
	YE: {
		languages: ['ar'],
		name: 'Yemen',
		original: 'اليمن',
		iso3: 'YEM',
	},
	YT: {
		languages: ['fr'],
		name: 'Mayotte',
		original: 'Mayotte',
		iso3: 'MYT',
	},
	ZA: {
		languages: ['af', 'en', 'nr', 'st', 'ss', 'tn', 'ts', 've', 'xh', 'zu'],
		name: 'South Africa',
		original: 'South Africa',
		iso3: 'ZAF',
	},
	ZM: {
		languages: ['en'],
		name: 'Zambia',
		original: 'Zambia',
		iso3: 'ZMB',
	},
	ZW: {
		languages: ['en', 'sn', 'nd'],
		name: 'Zimbabwe',
		original: 'Zimbabwe',
		iso3: 'ZWE',
	},
} as const

export type ISO3 = (typeof COUNTRIES_ISO)[keyof typeof COUNTRIES_ISO]['iso3']

// Country dialing codes mapped by ISO 2-letter code
export const COUNTRY_DIALING_CODES: Record<string, string> = {
	AD: '+376',
	AE: '+971',
	AF: '+93',
	AG: '+1268',
	AI: '+1264',
	AL: '+355',
	AM: '+374',
	AO: '+244',
	AQ: '+672',
	AR: '+54',
	AS: '+1684',
	AT: '+43',
	AU: '+61',
	AW: '+297',
	AX: '+358',
	AZ: '+994',
	BA: '+387',
	BB: '+1246',
	BD: '+880',
	BE: '+32',
	BF: '+226',
	BG: '+359',
	BH: '+973',
	BI: '+257',
	BJ: '+229',
	BL: '+590',
	BM: '+1441',
	BN: '+673',
	BO: '+591',
	BQ: '+599',
	BR: '+55',
	BS: '+1242',
	BT: '+975',
	BV: '+47',
	BW: '+267',
	BY: '+375',
	BZ: '+501',
	CA: '+1',
	CC: '+61',
	CD: '+243',
	CF: '+236',
	CG: '+242',
	CH: '+41',
	CI: '+225',
	CK: '+682',
	CL: '+56',
	CM: '+237',
	CN: '+86',
	CO: '+57',
	CR: '+506',
	CU: '+53',
	CV: '+238',
	CW: '+599',
	CX: '+61',
	CY: '+357',
	CZ: '+420',
	DE: '+49',
	DJ: '+253',
	DK: '+45',
	DM: '+1767',
	DO: '+1809',
	DZ: '+213',
	EC: '+593',
	EE: '+372',
	EG: '+20',
	EH: '+212',
	ER: '+291',
	ES: '+34',
	ET: '+251',
	FI: '+358',
	FJ: '+679',
	FK: '+500',
	FM: '+691',
	FO: '+298',
	FR: '+33',
	GA: '+241',
	GB: '+44',
	GD: '+1473',
	GE: '+995',
	GF: '+594',
	GG: '+44',
	GH: '+233',
	GI: '+350',
	GL: '+299',
	GM: '+220',
	GN: '+224',
	GP: '+590',
	GQ: '+240',
	GR: '+30',
	GS: '+500',
	GT: '+502',
	GU: '+1671',
	GW: '+245',
	GY: '+592',
	HK: '+852',
	HM: '+672',
	HN: '+504',
	HR: '+385',
	HT: '+509',
	HU: '+36',
	ID: '+62',
	IE: '+353',
	IL: '+972',
	IM: '+44',
	IN: '+91',
	IO: '+246',
	IQ: '+964',
	IR: '+98',
	IS: '+354',
	IT: '+39',
	JE: '+44',
	JM: '+1876',
	JO: '+962',
	JP: '+81',
	KE: '+254',
	KG: '+996',
	KH: '+855',
	KI: '+686',
	KM: '+269',
	KN: '+1869',
	KP: '+850',
	KR: '+82',
	KW: '+965',
	KY: '+1345',
	KZ: '+7',
	LA: '+856',
	LB: '+961',
	LC: '+1758',
	LI: '+423',
	LK: '+94',
	LR: '+231',
	LS: '+266',
	LT: '+370',
	LU: '+352',
	LV: '+371',
	LY: '+218',
	MA: '+212',
	MC: '+377',
	MD: '+373',
	ME: '+382',
	MF: '+590',
	MG: '+261',
	MH: '+692',
	MK: '+389',
	ML: '+223',
	MM: '+95',
	MN: '+976',
	MO: '+853',
	MP: '+1670',
	MQ: '+596',
	MR: '+222',
	MS: '+1664',
	MT: '+356',
	MU: '+230',
	MV: '+960',
	MW: '+265',
	MX: '+52',
	MY: '+60',
	MZ: '+258',
	NA: '+264',
	NC: '+687',
	NE: '+227',
	NF: '+672',
	NG: '+234',
	NI: '+505',
	NL: '+31',
	NO: '+47',
	NP: '+977',
	NR: '+674',
	NU: '+683',
	NZ: '+64',
	OM: '+968',
	PA: '+507',
	PE: '+51',
	PF: '+689',
	PG: '+675',
	PH: '+63',
	PK: '+92',
	PL: '+48',
	PM: '+508',
	PN: '+64',
	PR: '+1787',
	PS: '+970',
	PT: '+351',
	PW: '+680',
	PY: '+595',
	QA: '+974',
	RE: '+262',
	RO: '+40',
	RS: '+381',
	RU: '+7',
	RW: '+250',
	SA: '+966',
	SB: '+677',
	SC: '+248',
	SD: '+249',
	SE: '+46',
	SG: '+65',
	SH: '+290',
	SI: '+386',
	SJ: '+47',
	SK: '+421',
	SL: '+232',
	SM: '+378',
	SN: '+221',
	SO: '+252',
	SR: '+597',
	SS: '+211',
	ST: '+239',
	SV: '+503',
	SX: '+1721',
	SY: '+963',
	SZ: '+268',
	TC: '+1649',
	TD: '+235',
	TF: '+262',
	TG: '+228',
	TH: '+66',
	TJ: '+992',
	TK: '+690',
	TL: '+670',
	TM: '+993',
	TN: '+216',
	TO: '+676',
	TR: '+90',
	TT: '+1868',
	TV: '+688',
	TW: '+886',
	TZ: '+255',
	UA: '+380',
	UG: '+256',
	UM: '+1',
	US: '+1',
	UY: '+598',
	UZ: '+998',
	VA: '+379',
	VC: '+1784',
	VE: '+58',
	VG: '+1284',
	VI: '+1340',
	VN: '+84',
	VU: '+678',
	WF: '+681',
	WS: '+685',
	XK: '+383',
	YE: '+967',
	YT: '+262',
	ZA: '+27',
	ZM: '+260',
	ZW: '+263',
}

// Major cities by country name (for autocomplete)
export const CITIES_BY_COUNTRY: Record<string, string[]> = {
	'United States': [
		'New York',
		'Los Angeles',
		'Chicago',
		'Houston',
		'Phoenix',
		'Philadelphia',
		'San Antonio',
		'San Diego',
		'Dallas',
		'San Jose',
		'Austin',
		'Jacksonville',
		'Fort Worth',
		'Columbus',
		'Charlotte',
		'San Francisco',
		'Indianapolis',
		'Seattle',
		'Denver',
		'Washington',
		'Boston',
		'El Paso',
		'Nashville',
		'Detroit',
		'Portland',
		'Memphis',
		'Oklahoma City',
		'Las Vegas',
		'Louisville',
		'Baltimore',
		'Milwaukee',
		'Albuquerque',
		'Tucson',
		'Fresno',
		'Sacramento',
		'Mesa',
		'Kansas City',
		'Atlanta',
		'Miami',
		'Colorado Springs',
		'Raleigh',
		'Omaha',
		'Long Beach',
		'Virginia Beach',
		'Oakland',
		'Minneapolis',
		'Tulsa',
		'Tampa',
		'Arlington',
		'New Orleans',
	],
	'United Kingdom': [
		'London',
		'Birmingham',
		'Manchester',
		'Glasgow',
		'Liverpool',
		'Bristol',
		'Sheffield',
		'Leeds',
		'Edinburgh',
		'Leicester',
		'Coventry',
		'Bradford',
		'Cardiff',
		'Belfast',
		'Nottingham',
		'Kingston upon Hull',
		'Newcastle upon Tyne',
		'Stoke-on-Trent',
		'Southampton',
		'Derby',
		'Portsmouth',
		'Brighton',
		'Plymouth',
		'Northampton',
		'Reading',
		'Luton',
		'Wolverhampton',
		'Bolton',
		'Aberdeen',
		'Bournemouth',
		'Norwich',
		'Swindon',
		'Swansea',
		'Milton Keynes',
		'Southend-on-Sea',
		'Middlesbrough',
		'Peterborough',
		'Sunderland',
		'Warrington',
		'Huddersfield',
		'Oxford',
		'York',
		'Poole',
		'Ipswich',
		'Telford',
		'Cambridge',
		'Dundee',
		'Gloucester',
		'Blackpool',
		'Bath',
	],
	Germany: [
		'Berlin',
		'Hamburg',
		'Munich',
		'Cologne',
		'Frankfurt',
		'Stuttgart',
		'Düsseldorf',
		'Leipzig',
		'Dortmund',
		'Essen',
		'Bremen',
		'Dresden',
		'Hanover',
		'Nuremberg',
		'Duisburg',
		'Bochum',
		'Wuppertal',
		'Bielefeld',
		'Bonn',
		'Münster',
		'Mannheim',
		'Karlsruhe',
		'Augsburg',
		'Wiesbaden',
		'Mönchengladbach',
		'Gelsenkirchen',
		'Aachen',
		'Braunschweig',
		'Chemnitz',
		'Kiel',
		'Halle',
		'Magdeburg',
		'Freiburg',
		'Krefeld',
		'Mainz',
		'Lübeck',
		'Erfurt',
		'Oberhausen',
		'Rostock',
		'Kassel',
		'Hagen',
		'Potsdam',
		'Saarbrücken',
		'Hamm',
		'Ludwigshafen',
		'Oldenburg',
		'Mülheim',
		'Osnabrück',
		'Leverkusen',
		'Heidelberg',
	],
	France: [
		'Paris',
		'Marseille',
		'Lyon',
		'Toulouse',
		'Nice',
		'Nantes',
		'Strasbourg',
		'Montpellier',
		'Bordeaux',
		'Lille',
		'Rennes',
		'Reims',
		'Saint-Étienne',
		'Toulon',
		'Le Havre',
		'Grenoble',
		'Dijon',
		'Angers',
		'Nîmes',
		'Villeurbanne',
		'Saint-Denis',
		'Le Mans',
		'Clermont-Ferrand',
		'Aix-en-Provence',
		'Brest',
		'Tours',
		'Limoges',
		'Amiens',
		'Perpignan',
		'Metz',
		'Besançon',
		'Orléans',
		'Rouen',
		'Mulhouse',
		'Caen',
		'Nancy',
		'Saint-Paul',
		'Argenteuil',
		'Montreuil',
		'Roubaix',
		'Dunkirk',
		'Tourcoing',
		'Avignon',
		'Nanterre',
		'Poitiers',
		'Créteil',
		'Versailles',
		'Pau',
		'Courbevoie',
		'Vitry-sur-Seine',
	],
	Spain: [
		'Madrid',
		'Barcelona',
		'Valencia',
		'Seville',
		'Zaragoza',
		'Málaga',
		'Murcia',
		'Palma',
		'Las Palmas',
		'Bilbao',
		'Alicante',
		'Córdoba',
		'Valladolid',
		'Vigo',
		'Gijón',
		'Hospitalet de Llobregat',
		'A Coruña',
		'Vitoria-Gasteiz',
		'Granada',
		'Elche',
		'Oviedo',
		'Badalona',
		'Cartagena',
		'Terrassa',
		'Jerez de la Frontera',
		'Sabadell',
		'Santa Cruz de Tenerife',
		'Móstoles',
		'Alcalá de Henares',
		'Pamplona',
		'Fuenlabrada',
		'Almería',
		'Leganés',
		'San Sebastián',
		'Getafe',
		'Burgos',
		'Santander',
		'Albacete',
		'Castellón de la Plana',
		'Alcorcón',
		'San Cristóbal de La Laguna',
		'Logroño',
		'Badajoz',
		'Salamanca',
		'Huelva',
		'Marbella',
		'Lleida',
		'Tarragona',
		'León',
		'Cádiz',
	],
	Italy: [
		'Rome',
		'Milan',
		'Naples',
		'Turin',
		'Palermo',
		'Genoa',
		'Bologna',
		'Florence',
		'Bari',
		'Catania',
		'Venice',
		'Verona',
		'Messina',
		'Padua',
		'Trieste',
		'Taranto',
		'Brescia',
		'Parma',
		'Prato',
		'Modena',
		'Reggio Calabria',
		'Reggio Emilia',
		'Perugia',
		'Ravenna',
		'Livorno',
		'Cagliari',
		'Foggia',
		'Rimini',
		'Salerno',
		'Ferrara',
		'Sassari',
		'Latina',
		'Giugliano in Campania',
		'Monza',
		'Syracuse',
		'Pescara',
		'Bergamo',
		'Forlì',
		'Trento',
		'Vicenza',
		'Terni',
		'Bolzano',
		'Novara',
		'Piacenza',
		'Ancona',
		'Andria',
		'Arezzo',
		'Udine',
		'Cesena',
		'Lecce',
	],
	Netherlands: [
		'Amsterdam',
		'Rotterdam',
		'The Hague',
		'Utrecht',
		'Eindhoven',
		'Tilburg',
		'Groningen',
		'Almere',
		'Breda',
		'Nijmegen',
		'Apeldoorn',
		'Haarlem',
		'Arnhem',
		'Enschede',
		'Haarlemmermeer',
		'Zaanstad',
		'Amersfoort',
		's-Hertogenbosch',
		'Zwolle',
		'Zoetermeer',
		'Leiden',
		'Maastricht',
		'Dordrecht',
		'Ede',
		'Alphen aan den Rijn',
		'Westland',
		'Alkmaar',
		'Delft',
		'Emmen',
		'Deventer',
		'Venlo',
		'Sittard-Geleen',
		'Helmond',
		'Oss',
		'Hilversum',
		'Heerlen',
		'Amstelveen',
		'Purmerend',
		'Schiedam',
		'Spijkenisse',
		'Vlaardingen',
		'Almelo',
		'Gouda',
		'Leeuwarden',
		'Hoorn',
		'Velsen',
		'Hoeksche Waard',
		'Medemblik',
		'Roosendaal',
		'Hengelo',
	],
	Belgium: [
		'Brussels',
		'Antwerp',
		'Ghent',
		'Charleroi',
		'Liège',
		'Bruges',
		'Namur',
		'Leuven',
		'Mons',
		'Aalst',
		'Mechelen',
		'La Louvière',
		'Kortrijk',
		'Hasselt',
		'Ostend',
		'Sint-Niklaas',
		'Tournai',
		'Genk',
		'Seraing',
		'Roeselare',
		'Verviers',
		'Mouscron',
		'Dendermonde',
		'Beringen',
		'Turnhout',
		'Dilbeek',
		'Heist-op-den-Berg',
		'Sint-Truiden',
		'Lokeren',
		"Braine-l'Alleud",
		'Brasschaat',
		'Vilvoorde',
		'Waregem',
		'Châtelet',
		'Maasmechelen',
		'Ath',
		'Binche',
		'Herstal',
		'Grimbergen',
		'Halle',
		'Wavre',
		'Knokke-Heist',
		'Schoten',
		'Soignies',
		'Wevelgem',
		'Evergem',
		'Dour',
		'Ieper',
		'Ninove',
		'Geraardsbergen',
	],
	Switzerland: [
		'Zürich',
		'Geneva',
		'Basel',
		'Lausanne',
		'Bern',
		'Winterthur',
		'Lucerne',
		'St. Gallen',
		'Lugano',
		'Biel',
		'Thun',
		'Köniz',
		'La Chaux-de-Fonds',
		'Fribourg',
		'Schaffhausen',
		'Chur',
		'Vernier',
		'Neuchâtel',
		'Uster',
		'Sion',
		'Lancy',
		'Emmen',
		'Yverdon-les-Bains',
		'Zug',
		'Kriens',
		'Rapperswil-Jona',
		'Dübendorf',
		'Montreux',
		'Dietikon',
		'Frauenfeld',
		'Wetzikon',
		'Carouge',
		'Wädenswil',
		'Meyrin',
		'Riehen',
		'Allschwil',
		'Wil',
		'Renens',
		'Nyon',
		'Vevey',
		'Baar',
		'Kloten',
		'Bellinzona',
		'Baden',
		'Onex',
		'Volketswil',
		'Gossau',
		'Sierre',
		'Locarno',
		'Olten',
	],
	Austria: [
		'Vienna',
		'Graz',
		'Linz',
		'Salzburg',
		'Innsbruck',
		'Klagenfurt',
		'Villach',
		'Wels',
		'Sankt Pölten',
		'Dornbirn',
		'Wiener Neustadt',
		'Steyr',
		'Feldkirch',
		'Bregenz',
		'Leonding',
		'Klosterneuburg',
		'Baden bei Wien',
		'Wolfsberg',
		'Leoben',
		'Krems',
		'Traun',
		'Amstetten',
		'Lustenau',
		'Kapfenberg',
		'Mödling',
		'Hallein',
		'Kufstein',
		'Traiskirchen',
		'Schwechat',
		'Braunau am Inn',
		'Stockerau',
		'Saalfelden',
		'Ansfelden',
		'Tulln',
		'Hohenems',
		'Spittal an der Drau',
		'Telfs',
		'Ternitz',
		'Perchtoldsdorf',
		'Feldkirchen',
		'Bludenz',
		'Bad Ischl',
		'Eisenstadt',
		'Schwaz',
		'Hall in Tirol',
		'Gmunden',
		'Wörgl',
		'Waidhofen',
		'Knittelfeld',
		'Korneuburg',
	],
	Poland: [
		'Warsaw',
		'Kraków',
		'Łódź',
		'Wrocław',
		'Poznań',
		'Gdańsk',
		'Szczecin',
		'Bydgoszcz',
		'Lublin',
		'Białystok',
		'Katowice',
		'Gdynia',
		'Częstochowa',
		'Radom',
		'Toruń',
		'Sosnowiec',
		'Rzeszów',
		'Kielce',
		'Gliwice',
		'Olsztyn',
		'Zabrze',
		'Bielsko-Biała',
		'Bytom',
		'Zielona Góra',
		'Rybnik',
		'Ruda Śląska',
		'Opole',
		'Tychy',
		'Gorzów Wielkopolski',
		'Elbląg',
		'Płock',
		'Dąbrowa Górnicza',
		'Wałbrzych',
		'Włocławek',
		'Tarnów',
		'Chorzów',
		'Koszalin',
		'Kalisz',
		'Legnica',
		'Grudziądz',
		'Jaworzno',
		'Słupsk',
		'Jastrzębie-Zdrój',
		'Nowy Sącz',
		'Jelenia Góra',
		'Siedlce',
		'Mysłowice',
		'Konin',
		'Piła',
		'Piotrków Trybunalski',
	],
	Canada: [
		'Toronto',
		'Montreal',
		'Vancouver',
		'Calgary',
		'Edmonton',
		'Ottawa',
		'Winnipeg',
		'Quebec City',
		'Hamilton',
		'Kitchener',
		'London',
		'Victoria',
		'Halifax',
		'Oshawa',
		'Windsor',
		'Saskatoon',
		'Regina',
		'Sherbrooke',
		"St. John's",
		'Barrie',
		'Kelowna',
		'Abbotsford',
		'Sudbury',
		'Kingston',
		'Saguenay',
		'Trois-Rivières',
		'Guelph',
		'Moncton',
		'Brantford',
		'Saint John',
		'Thunder Bay',
		'Peterborough',
		'Lethbridge',
		'Nanaimo',
		'Kamloops',
		'Belleville',
		'Chatham-Kent',
		'Fredericton',
		'Red Deer',
		'Prince George',
		'Sarnia',
		'Drummondville',
		'Sault Ste. Marie',
		'Saint-Jean-sur-Richelieu',
		'Granby',
		'Medicine Hat',
		'North Bay',
		'St. Albert',
		'Welland',
		'Grande Prairie',
	],
	Australia: [
		'Sydney',
		'Melbourne',
		'Brisbane',
		'Perth',
		'Adelaide',
		'Gold Coast',
		'Newcastle',
		'Canberra',
		'Sunshine Coast',
		'Wollongong',
		'Hobart',
		'Geelong',
		'Townsville',
		'Cairns',
		'Darwin',
		'Toowoomba',
		'Ballarat',
		'Bendigo',
		'Albury',
		'Launceston',
		'Mackay',
		'Rockhampton',
		'Bunbury',
		'Bundaberg',
		'Coffs Harbour',
		'Wagga Wagga',
		'Hervey Bay',
		'Mildura',
		'Shepparton',
		'Gladstone',
		'Port Macquarie',
		'Tamworth',
		'Traralgon',
		'Orange',
		'Bowral',
		'Geraldton',
		'Dubbo',
		'Nowra',
		'Bathurst',
		'Warrnambool',
		'Albany',
		'Kalgoorlie',
		'Devonport',
		'Mount Gambier',
		'Lismore',
		'Nelson Bay',
		'Maitland',
		'Burnie',
		'Alice Springs',
		'Armidale',
	],
	Japan: [
		'Tokyo',
		'Yokohama',
		'Osaka',
		'Nagoya',
		'Sapporo',
		'Fukuoka',
		'Kobe',
		'Kawasaki',
		'Kyoto',
		'Saitama',
		'Hiroshima',
		'Sendai',
		'Kitakyushu',
		'Chiba',
		'Sakai',
		'Niigata',
		'Hamamatsu',
		'Shizuoka',
		'Sagamihara',
		'Okayama',
		'Kumamoto',
		'Kagoshima',
		'Funabashi',
		'Hachioji',
		'Kawaguchi',
		'Himeji',
		'Matsuyama',
		'Utsunomiya',
		'Higashiosaka',
		'Matsudo',
		'Nishinomiya',
		'Ichikawa',
		'Kurashiki',
		'Oita',
		'Kanazawa',
		'Amagasaki',
		'Nagasaki',
		'Toyama',
		'Yokosuka',
		'Gifu',
		'Hirakata',
		'Machida',
		'Toyota',
		'Fujisawa',
		'Kashiwa',
		'Takamatsu',
		'Toyonaka',
		'Nagano',
		'Toyohashi',
		'Wakayama',
	],
	China: [
		'Shanghai',
		'Beijing',
		'Guangzhou',
		'Shenzhen',
		'Chengdu',
		'Tianjin',
		'Wuhan',
		'Dongguan',
		'Chongqing',
		'Nanjing',
		'Shenyang',
		'Hangzhou',
		"Xi'an",
		'Harbin',
		'Suzhou',
		'Qingdao',
		'Dalian',
		'Zhengzhou',
		'Shantou',
		'Jinan',
		'Changchun',
		'Kunming',
		'Changsha',
		'Taiyuan',
		'Xiamen',
		'Hefei',
		'Shijiazhuang',
		'Ürümqi',
		'Fuzhou',
		'Wuxi',
		'Zhongshan',
		'Wenzhou',
		'Nanning',
		'Nanchang',
		'Ningbo',
		'Guiyang',
		'Lanzhou',
		'Zibo',
		'Changzhou',
		'Xuzhou',
		'Tangshan',
		'Baotou',
		'Huizhou',
		'Yantai',
		'Luoyang',
		'Linyi',
		'Weifang',
		'Nantong',
		'Yangzhou',
		'Haikou',
	],
	India: [
		'Mumbai',
		'Delhi',
		'Bangalore',
		'Hyderabad',
		'Ahmedabad',
		'Chennai',
		'Kolkata',
		'Surat',
		'Pune',
		'Jaipur',
		'Lucknow',
		'Kanpur',
		'Nagpur',
		'Indore',
		'Thane',
		'Bhopal',
		'Visakhapatnam',
		'Pimpri-Chinchwad',
		'Patna',
		'Vadodara',
		'Ghaziabad',
		'Ludhiana',
		'Agra',
		'Nashik',
		'Faridabad',
		'Meerut',
		'Rajkot',
		'Kalyan-Dombivli',
		'Vasai-Virar',
		'Varanasi',
		'Srinagar',
		'Aurangabad',
		'Dhanbad',
		'Amritsar',
		'Navi Mumbai',
		'Allahabad',
		'Howrah',
		'Ranchi',
		'Gwalior',
		'Jabalpur',
		'Coimbatore',
		'Vijayawada',
		'Jodhpur',
		'Madurai',
		'Raipur',
		'Kota',
		'Chandigarh',
		'Guwahati',
		'Solapur',
		'Hubli-Dharwad',
	],
	Brazil: [
		'São Paulo',
		'Rio de Janeiro',
		'Brasília',
		'Salvador',
		'Fortaleza',
		'Belo Horizonte',
		'Manaus',
		'Curitiba',
		'Recife',
		'Goiânia',
		'Porto Alegre',
		'Belém',
		'Guarulhos',
		'Campinas',
		'São Luís',
		'São Gonçalo',
		'Maceió',
		'Duque de Caxias',
		'Campo Grande',
		'Natal',
		'Teresina',
		'São Bernardo do Campo',
		'Nova Iguaçu',
		'João Pessoa',
		'Santo André',
		'São José dos Campos',
		'Ribeirão Preto',
		'Jaboatão dos Guararapes',
		'Osasco',
		'Uberlândia',
		'Sorocaba',
		'Contagem',
		'Aracaju',
		'Feira de Santana',
		'Cuiabá',
		'Joinville',
		'Aparecida de Goiânia',
		'Londrina',
		'Juiz de Fora',
		'Ananindeua',
		'Porto Velho',
		'Niterói',
		'Serra',
		'Belford Roxo',
		'Caxias do Sul',
		'Macapá',
		'Florianópolis',
		'Vila Velha',
		'Mauá',
		'São João de Meriti',
	],
	Mexico: [
		'Mexico City',
		'Guadalajara',
		'Monterrey',
		'Puebla',
		'Tijuana',
		'León',
		'Juárez',
		'Zapopan',
		'Mérida',
		'San Luis Potosí',
		'Aguascalientes',
		'Hermosillo',
		'Saltillo',
		'Mexicali',
		'Culiacán',
		'Querétaro',
		'Chihuahua',
		'Naucalpan',
		'Tlalnepantla',
		'Guadalupe',
		'Acapulco',
		'Tlaquepaque',
		'Cancún',
		'Morelia',
		'Chimalhuacán',
		'Torreón',
		'Victoria',
		'Reynosa',
		'Tuxtla Gutiérrez',
		'Ecatepec',
		'Durango',
		'Irapuato',
		'Toluca',
		'Matamoros',
		'Villahermosa',
		'Cuautitlán Izcalli',
		'Mazatlán',
		'Nuevo Laredo',
		'Tonalá',
		'Veracruz',
		'Xalapa',
		'Ciudad López Mateos',
		'Oaxaca',
		'Celaya',
		'Ensenada',
		'Tepic',
		'Pachuca',
		'Cuernavaca',
		'Ciudad Obregón',
		'La Paz',
	],
	Argentina: [
		'Buenos Aires',
		'Córdoba',
		'Rosario',
		'Mendoza',
		'San Miguel de Tucumán',
		'La Plata',
		'Mar del Plata',
		'Salta',
		'Santa Fe',
		'San Juan',
		'Resistencia',
		'Neuquén',
		'Santiago del Estero',
		'Corrientes',
		'Bahía Blanca',
		'San Salvador de Jujuy',
		'Posadas',
		'Paraná',
		'Formosa',
		'San Fernando del Valle de Catamarca',
		'Río Cuarto',
		'Comodoro Rivadavia',
		'San Luis',
		'La Rioja',
		'Concordia',
		'San Nicolás de los Arroyos',
		'San Rafael',
		'Tandil',
		'Villa María',
		'San Carlos de Bariloche',
		'Cipolletti',
		'Pergamino',
		'Olavarría',
		'Junín',
		'Rafaela',
		'Necochea',
		'Río Gallegos',
		'Trelew',
		'Ushuaia',
		'Gualeguaychú',
		'Venado Tuerto',
		'San Martín de los Andes',
		'Zárate',
		'Mercedes',
		'Luján',
		'Campana',
		'General Roca',
		'Chivilcoy',
		'Rawson',
		'Puerto Madryn',
	],
	'South Africa': [
		'Johannesburg',
		'Cape Town',
		'Durban',
		'Pretoria',
		'Port Elizabeth',
		'Bloemfontein',
		'East London',
		'Polokwane',
		'Nelspruit',
		'Kimberley',
		'Rustenburg',
		'Pietermaritzburg',
		'Potchefstroom',
		'Klerksdorp',
		'George',
		'Stellenbosch',
		'Witbank',
		'Upington',
		'Worcester',
		'Middelburg',
		'Paarl',
		'Grahamstown',
		'Vereeniging',
		'Richards Bay',
		'Newcastle',
		'Welkom',
		'Oudtshoorn',
		'Vanderbijlpark',
		'Secunda',
		'Standerton',
		'Mossel Bay',
		'Knysna',
		'Bethlehem',
		'Queenstown',
		'Mahikeng',
		'Sasolburg',
		'Ladysmith',
		'Kroonstad',
		'Springs',
		'Boksburg',
		'Midrand',
		'Sandton',
		'Centurion',
		'Alberton',
		'Benoni',
		'Kempton Park',
		'Brakpan',
		'Germiston',
		'Edenvale',
		'Randburg',
	],
	Russia: [
		'Moscow',
		'Saint Petersburg',
		'Novosibirsk',
		'Yekaterinburg',
		'Kazan',
		'Nizhny Novgorod',
		'Chelyabinsk',
		'Samara',
		'Omsk',
		'Rostov-on-Don',
		'Ufa',
		'Krasnoyarsk',
		'Voronezh',
		'Perm',
		'Volgograd',
	],
	Portugal: [
		'Lisbon',
		'Porto',
		'Vila Nova de Gaia',
		'Amadora',
		'Braga',
		'Setúbal',
		'Coimbra',
		'Queluz',
		'Funchal',
		'Cacém',
		'Vila Franca de Xira',
		'Almada',
		'Agualva-Cacém',
		'Leiria',
		'Guimarães',
		'Évora',
		'Odivelas',
		'Rio Tinto',
		'Viseu',
		'Matosinhos',
	],
	Greece: [
		'Athens',
		'Thessaloniki',
		'Patras',
		'Piraeus',
		'Larissa',
		'Heraklion',
		'Peristeri',
		'Kallithea',
		'Acharnes',
		'Kalamaria',
		'Nikaia',
		'Glyfada',
		'Volos',
		'Ilio',
		'Ilioupoli',
		'Keratsini',
		'Nea Smyrni',
		'Amarousion',
		'Zografos',
		'Chalandri',
	],
	Sweden: [
		'Stockholm',
		'Gothenburg',
		'Malmö',
		'Uppsala',
		'Västerås',
		'Örebro',
		'Linköping',
		'Helsingborg',
		'Jönköping',
		'Norrköping',
		'Lund',
		'Umeå',
		'Gävle',
		'Borås',
		'Eskilstuna',
		'Södertälje',
		'Karlstad',
		'Täby',
		'Växjö',
		'Halmstad',
	],
	Norway: [
		'Oslo',
		'Bergen',
		'Trondheim',
		'Stavanger',
		'Drammen',
		'Fredrikstad',
		'Kristiansand',
		'Sandnes',
		'Tromsø',
		'Sarpsborg',
		'Skien',
		'Ålesund',
		'Sandefjord',
		'Haugesund',
		'Tønsberg',
		'Moss',
		'Porsgrunn',
		'Bodø',
		'Arendal',
		'Hamar',
	],
	Denmark: [
		'Copenhagen',
		'Aarhus',
		'Odense',
		'Aalborg',
		'Frederiksberg',
		'Esbjerg',
		'Randers',
		'Kolding',
		'Horsens',
		'Vejle',
		'Roskilde',
		'Herning',
		'Hørsholm',
		'Helsingør',
		'Silkeborg',
		'Næstved',
		'Greve',
		'Tårnby',
		'Fredericia',
		'Viborg',
	],
	Finland: [
		'Helsinki',
		'Espoo',
		'Tampere',
		'Vantaa',
		'Oulu',
		'Turku',
		'Jyväskylä',
		'Lahti',
		'Kuopio',
		'Pori',
		'Kouvola',
		'Joensuu',
		'Lappeenranta',
		'Hämeenlinna',
		'Vaasa',
		'Seinäjoki',
		'Rovaniemi',
		'Mikkeli',
		'Kotka',
		'Salo',
	],
	Ireland: [
		'Dublin',
		'Cork',
		'Limerick',
		'Galway',
		'Waterford',
		'Drogheda',
		'Swords',
		'Dundalk',
		'Bray',
		'Navan',
		'Kilkenny',
		'Ennis',
		'Carlow',
		'Tralee',
		'Newbridge',
		'Portlaoise',
		'Mullingar',
		'Wexford',
		'Letterkenny',
		'Sligo',
	],
	'New Zealand': [
		'Auckland',
		'Wellington',
		'Christchurch',
		'Hamilton',
		'Tauranga',
		'Napier-Hastings',
		'Dunedin',
		'Palmerston North',
		'Nelson',
		'Rotorua',
		'New Plymouth',
		'Whangarei',
		'Invercargill',
		'Whanganui',
		'Gisborne',
		'Blenheim',
		'Pukekohe',
		'Timaru',
		'Taupo',
		'Masterton',
	],
	Singapore: ['Singapore'],
	'Hong Kong': ['Hong Kong', 'Kowloon', 'Tsuen Wan', 'Yuen Long', 'Tai Po', 'Sha Tin', 'Sai Kung', 'Tuen Mun', 'Lantau Island', 'Central'],
	'South Korea': [
		'Seoul',
		'Busan',
		'Incheon',
		'Daegu',
		'Daejeon',
		'Gwangju',
		'Suwon',
		'Ulsan',
		'Changwon',
		'Goyang',
		'Yongin',
		'Seongnam',
		'Bucheon',
		'Cheongju',
		'Ansan',
		'Jeonju',
		'Cheonan',
		'Anyang',
		'Hwaseong',
		'Gimhae',
	],
	Taiwan: [
		'Taipei',
		'Kaohsiung',
		'Taichung',
		'Tainan',
		'Banqiao',
		'Hsinchu',
		'Taoyuan',
		'Keelung',
		'Zhongli',
		'Chiayi',
		'Changhua',
		'Pingtung',
		'Yilan',
		'Hualien',
		'Nantou',
		'Yunlin',
		'Taitung',
		'Miaoli',
		'Penghu',
		'Kinmen',
	],
	Thailand: [
		'Bangkok',
		'Nonthaburi',
		'Nakhon Ratchasima',
		'Chiang Mai',
		'Hat Yai',
		'Udon Thani',
		'Pak Kret',
		'Khon Kaen',
		'Chaophraya Surasak',
		'Surat Thani',
		'Nakhon Si Thammarat',
		'Nakhon Sawan',
		'Ubon Ratchathani',
		'Songkhla',
		'Nakhon Pathom',
		'Phuket',
		'Pattaya',
		'Rayong',
		'Chonburi',
		'Samut Prakan',
	],
	Malaysia: [
		'Kuala Lumpur',
		'George Town',
		'Ipoh',
		'Shah Alam',
		'Petaling Jaya',
		'Johor Bahru',
		'Melaka',
		'Kota Kinabalu',
		'Kuching',
		'Seremban',
		'Subang Jaya',
		'Kuantan',
		'Iskandar Puteri',
		'Klang',
		'Kajang',
		'Ampang',
		'Alor Setar',
		'Kota Bharu',
		'Miri',
		'Kuala Terengganu',
	],
	Indonesia: [
		'Jakarta',
		'Surabaya',
		'Bandung',
		'Medan',
		'Semarang',
		'Palembang',
		'Makassar',
		'Tangerang',
		'Depok',
		'Bekasi',
		'Padang',
		'Bandar Lampung',
		'Malang',
		'Pekanbaru',
		'Batam',
		'Bogor',
		'Denpasar',
		'Yogyakarta',
		'Manado',
		'Banjarmasin',
	],
	Philippines: [
		'Manila',
		'Quezon City',
		'Davao City',
		'Caloocan',
		'Cebu City',
		'Zamboanga City',
		'Taguig',
		'Antipolo',
		'Pasig',
		'Cagayan de Oro',
		'Parañaque',
		'Dasmariñas',
		'Valenzuela',
		'Bacoor',
		'General Santos',
		'Las Piñas',
		'Makati',
		'Bacolod',
		'Muntinlupa',
		'Iloilo City',
	],
	Vietnam: [
		'Ho Chi Minh City',
		'Hanoi',
		'Da Nang',
		'Hai Phong',
		'Can Tho',
		'Bien Hoa',
		'Nha Trang',
		'Hue',
		'Buon Ma Thuot',
		'Vung Tau',
		'Quy Nhon',
		'Thai Nguyen',
		'Nam Dinh',
		'Long Xuyen',
		'Ha Long',
		'Pleiku',
		'Phan Thiet',
		'Cam Ranh',
		'Vinh',
		'Da Lat',
	],
	Turkey: [
		'Istanbul',
		'Ankara',
		'Izmir',
		'Bursa',
		'Adana',
		'Gaziantep',
		'Konya',
		'Antalya',
		'Kayseri',
		'Mersin',
		'Eskişehir',
		'Diyarbakır',
		'Samsun',
		'Denizli',
		'Şanlıurfa',
		'Adapazarı',
		'Malatya',
		'Kahramanmaraş',
		'Erzurum',
		'Van',
	],
	Israel: [
		'Jerusalem',
		'Tel Aviv',
		'Haifa',
		'Rishon LeZion',
		'Petah Tikva',
		'Ashdod',
		'Netanya',
		'Beer Sheva',
		'Holon',
		'Bnei Brak',
		'Ramat Gan',
		'Ashkelon',
		'Rehovot',
		'Bat Yam',
		'Beit Shemesh',
		'Kfar Saba',
		'Herzliya',
		'Hadera',
		'Modiin',
		'Nazareth',
	],
	'United Arab Emirates': [
		'Dubai',
		'Abu Dhabi',
		'Sharjah',
		'Al Ain',
		'Ajman',
		'Ras Al Khaimah',
		'Fujairah',
		'Umm Al Quwain',
		'Khor Fakkan',
		'Dibba Al-Fujairah',
	],
	'Saudi Arabia': [
		'Riyadh',
		'Jeddah',
		'Mecca',
		'Medina',
		'Dammam',
		'Khobar',
		'Taif',
		'Tabuk',
		'Buraidah',
		'Khamis Mushait',
		'Abha',
		'Najran',
		'Jubail',
		'Yanbu',
		'Hofuf',
		'Hail',
		'Jizan',
		'Al Qatif',
		'Dhahran',
		'Arar',
	],
	Egypt: [
		'Cairo',
		'Alexandria',
		'Giza',
		'Shubra El Kheima',
		'Port Said',
		'Suez',
		'Luxor',
		'Asyut',
		'Mansoura',
		'Tanta',
		'Faiyum',
		'Zagazig',
		'Ismailia',
		'Aswan',
		'Kafr el-Sheikh',
		'Damanhur',
		'Minya',
		'Damietta',
		'Beni Suef',
		'Hurghada',
	],
	Nigeria: [
		'Lagos',
		'Kano',
		'Ibadan',
		'Abuja',
		'Port Harcourt',
		'Benin City',
		'Maiduguri',
		'Zaria',
		'Aba',
		'Jos',
		'Ilorin',
		'Oyo',
		'Enugu',
		'Abeokuta',
		'Sokoto',
		'Onitsha',
		'Warri',
		'Kaduna',
		'Calabar',
		'Uyo',
	],
	Kenya: [
		'Nairobi',
		'Mombasa',
		'Kisumu',
		'Nakuru',
		'Eldoret',
		'Ruiru',
		'Kikuyu',
		'Kangundo-Tala',
		'Malindi',
		'Naivasha',
		'Kitui',
		'Machakos',
		'Thika',
		'Karuri',
		'Nyeri',
		'Garissa',
		'Kakamega',
		'Meru',
		'Lamu',
		'Kisii',
	],
	'Czech Republic': [
		'Prague',
		'Brno',
		'Ostrava',
		'Plzeň',
		'Liberec',
		'Olomouc',
		'České Budějovice',
		'Hradec Králové',
		'Ústí nad Labem',
		'Pardubice',
		'Zlín',
		'Havířov',
		'Kladno',
		'Most',
		'Opava',
		'Frýdek-Místek',
		'Karviná',
		'Jihlava',
		'Teplice',
		'Děčín',
	],
	Hungary: [
		'Budapest',
		'Debrecen',
		'Szeged',
		'Miskolc',
		'Pécs',
		'Győr',
		'Nyíregyháza',
		'Kecskemét',
		'Székesfehérvár',
		'Szombathely',
		'Szolnok',
		'Tatabánya',
		'Kaposvár',
		'Érd',
		'Veszprém',
		'Békéscsaba',
		'Zalaegerszeg',
		'Sopron',
		'Eger',
		'Nagykanizsa',
	],
	Romania: [
		'Bucharest',
		'Cluj-Napoca',
		'Timișoara',
		'Iași',
		'Constanța',
		'Craiova',
		'Brașov',
		'Galați',
		'Ploiești',
		'Oradea',
		'Brăila',
		'Arad',
		'Pitești',
		'Sibiu',
		'Bacău',
		'Târgu Mureș',
		'Baia Mare',
		'Buzău',
		'Botoșani',
		'Satu Mare',
	],
	Croatia: [
		'Zagreb',
		'Split',
		'Rijeka',
		'Osijek',
		'Zadar',
		'Pula',
		'Slavonski Brod',
		'Karlovac',
		'Varaždin',
		'Šibenik',
		'Sisak',
		'Vinkovci',
		'Velika Gorica',
		'Dubrovnik',
		'Bjelovar',
		'Koprivnica',
		'Samobor',
		'Vukovar',
		'Požega',
		'Đakovo',
	],
	Slovakia: [
		'Bratislava',
		'Košice',
		'Prešov',
		'Žilina',
		'Nitra',
		'Banská Bystrica',
		'Trnava',
		'Martin',
		'Trenčín',
		'Poprad',
		'Prievidza',
		'Zvolen',
		'Považská Bystrica',
		'Michalovce',
		'Nové Zámky',
		'Spišská Nová Ves',
		'Komárno',
		'Humenné',
		'Levice',
		'Bardejov',
	],
	Slovenia: [
		'Ljubljana',
		'Maribor',
		'Celje',
		'Kranj',
		'Velenje',
		'Koper',
		'Novo Mesto',
		'Ptuj',
		'Trbovlje',
		'Kamnik',
		'Jesenice',
		'Nova Gorica',
		'Domžale',
		'Škofja Loka',
		'Murska Sobota',
		'Izola',
		'Postojna',
		'Krško',
		'Ravne na Koroškem',
		'Logatec',
	],
	Bulgaria: [
		'Sofia',
		'Plovdiv',
		'Varna',
		'Burgas',
		'Ruse',
		'Stara Zagora',
		'Pleven',
		'Sliven',
		'Dobrich',
		'Shumen',
		'Pernik',
		'Haskovo',
		'Yambol',
		'Pazardzhik',
		'Blagoevgrad',
		'Veliko Tarnovo',
		'Vratsa',
		'Gabrovo',
		'Asenovgrad',
		'Vidin',
	],
	Serbia: [
		'Belgrade',
		'Novi Sad',
		'Niš',
		'Kragujevac',
		'Subotica',
		'Zrenjanin',
		'Pančevo',
		'Čačak',
		'Novi Pazar',
		'Kraljevo',
		'Smederevo',
		'Leskovac',
		'Užice',
		'Vranje',
		'Valjevo',
		'Šabac',
		'Kruševac',
		'Sombor',
		'Požarevac',
		'Pirot',
	],
	Ukraine: [
		'Kyiv',
		'Kharkiv',
		'Odesa',
		'Dnipro',
		'Donetsk',
		'Zaporizhzhia',
		'Lviv',
		'Kryvyi Rih',
		'Mykolaiv',
		'Mariupol',
		'Luhansk',
		'Vinnytsia',
		'Makiivka',
		'Simferopol',
		'Kherson',
		'Poltava',
		'Chernihiv',
		'Cherkasy',
		'Sumy',
		'Zhytomyr',
	],
	Lithuania: [
		'Vilnius',
		'Kaunas',
		'Klaipėda',
		'Šiauliai',
		'Panevėžys',
		'Alytus',
		'Marijampolė',
		'Mažeikiai',
		'Jonava',
		'Utena',
		'Kėdainiai',
		'Telšiai',
		'Visaginas',
		'Tauragė',
		'Ukmergė',
		'Plungė',
		'Kretinga',
		'Šilutė',
		'Radviliškis',
		'Palanga',
	],
	Latvia: [
		'Riga',
		'Daugavpils',
		'Liepāja',
		'Jelgava',
		'Jūrmala',
		'Ventspils',
		'Rēzekne',
		'Valmiera',
		'Ogre',
		'Jēkabpils',
		'Tukums',
		'Salaspils',
		'Cēsis',
		'Kuldīga',
		'Olaine',
		'Saldus',
		'Talsi',
		'Sigulda',
		'Dobele',
		'Krāslava',
	],
	Estonia: [
		'Tallinn',
		'Tartu',
		'Narva',
		'Pärnu',
		'Kohtla-Järve',
		'Viljandi',
		'Rakvere',
		'Maardu',
		'Sillamäe',
		'Kuressaare',
		'Võru',
		'Valga',
		'Jõhvi',
		'Haapsalu',
		'Keila',
		'Paide',
		'Tapa',
		'Põlva',
		'Kiviõli',
		'Elva',
	],
}

export const PRODUCT_CATEGORIES = [
	'Bitcoin',
	'Art',
	'Clothing',
	'Food & Drink',
	'Home & Technology',
	'Health & Beauty',
	'Sports & Outside',
	'Services',
	'Other',
] as const

export type ProductCategory = (typeof PRODUCT_CATEGORIES)[number]

export const SHIPPING_TEMPLATES = [
	{
		name: 'Local Pickup',
		cost: '0',
		countries: [],
	},
	{
		name: 'Worldwide Standard',
		cost: '0',
		countries: null,
	},
	{
		name: 'Europe Standard',
		cost: '8.00',
		countries: [
			'AUT',
			'BEL',
			'BGR',
			'HRV',
			'CYP',
			'CZE',
			'DNK',
			'EST',
			'FIN',
			'FRA',
			'DEU',
			'GRC',
			'HUN',
			'IRL',
			'ITA',
			'LVA',
			'LTU',
			'LUX',
			'MLT',
			'NLD',
			'POL',
			'PRT',
			'ROU',
			'SVK',
			'SVN',
			'ESP',
			'SWE',
		],
	},
	{
		name: 'Digital Delivery',
		cost: '0',
		countries: null,
	},
	{
		name: 'Express Worldwide',
		cost: '0',
		countries: null,
	},
	{
		name: 'North America',
		cost: '0',
		countries: ['USA', 'CAN', 'MEX'],
	},
	{
		name: 'South America',
		cost: '0',
		countries: ['ARG', 'BOL', 'BRA', 'CHL', 'COL', 'ECU', 'GUF', 'GUY', 'PRY', 'PER', 'SUR', 'URY', 'VEN'],
	},
	{
		name: 'Asia Pacific',
		cost: '0',
		countries: ['CHN', 'JPN', 'KOR', 'TWN', 'HKG', 'SGP', 'THA', 'VNM', 'MYS', 'IDN', 'PHL', 'AUS', 'NZL'],
	},
	{
		name: 'Middle East',
		cost: '0',
		countries: ['ARE', 'SAU', 'BHR', 'OMN', 'QAT', 'KWT', 'IRN', 'IRQ', 'ISR', 'JOR', 'LBN', 'SYR'],
	},
	{
		name: 'Africa Standard',
		cost: '0',
		countries: ['ZAF', 'EGY', 'MAR', 'TUN', 'DZA', 'NGA', 'KEN', 'GHA', 'SEN', 'TZA', 'UGA', 'ETH'],
	},
	{
		name: 'UK & Ireland',
		cost: '0',
		countries: ['GBR', 'IRL'],
	},
	{
		name: 'Scandinavia',
		cost: '0',
		countries: ['DNK', 'FIN', 'ISL', 'NOR', 'SWE'],
	},
	{
		name: 'Oceania',
		cost: '0',
		countries: ['AUS', 'NZL', 'FJI', 'PNG', 'SLB', 'VUT', 'NCL', 'WSM'],
	},
	{
		name: 'DACH Region',
		cost: '0',
		countries: ['DEU', 'AUT', 'CHE'],
	},
	{
		name: 'Benelux',
		cost: '0',
		countries: ['BEL', 'NLD', 'LUX'],
	},
	{
		name: 'Mediterranean',
		cost: '0',
		countries: ['ESP', 'PRT', 'ITA', 'GRC', 'MLT', 'CYP', 'HRV', 'SVN'],
	},
]
